import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CreateNewsData, UpdateNewsData, newsApi } from '@/services';

export const adminNewsKeys = {
  all: ['admin', 'news'] as const,
  list: (page = 1, limit = 100, categoryId?: string) =>
    [...adminNewsKeys.all, 'list', { page, limit, categoryId }] as const,
  categories: ['admin', 'news', 'categories'] as const,
};

export function useAdminNewsListQuery(page = 1, limit = 100, categoryId?: string) {
  return useQuery({
    queryKey: adminNewsKeys.list(page, limit, categoryId),
    queryFn: () => newsApi.getAll(page, limit, categoryId),
  });
}

export function useAdminNewsQuery(id?: string) {
  return useQuery({
    queryKey: [...adminNewsKeys.all, 'detail', id] as const,
    queryFn: () => newsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateNewsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateNewsData) => newsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminNewsKeys.all });
    },
  });
}

export function useUpdateNewsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNewsData }) => newsApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminNewsKeys.all });
      queryClient.invalidateQueries({
        queryKey: [...adminNewsKeys.all, 'detail', variables.id],
      });
    },
  });
}

export function useDeleteNewsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => newsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminNewsKeys.all });
    },
  });
}

export function useToggleNewsPublishedMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => newsApi.togglePublished(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminNewsKeys.all });
    },
  });
}

export function useAdminNewsCategoriesQuery() {
  return useQuery({
    queryKey: adminNewsKeys.categories,
    queryFn: () => newsApi.getManagedCategories(),
  });
}

export function useCreateNewsCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; color?: string }) => newsApi.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminNewsKeys.all });
    },
  });
}

export function useUpdateNewsCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, color }: { id: string; name: string; color?: string }) =>
      newsApi.updateCategory(id, { name, color }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminNewsKeys.all });
    },
  });
}

export function useDeleteNewsCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => newsApi.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminNewsKeys.all });
    },
  });
}
